import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceHolder from 'react-placeholder';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchMcpClients, updateApiClientActiveState } from 'commons/src/actions/apiClientActions';
import { analyticsLogger, IntegrationType, PageType } from 'commons/src/analytics';
import { INTEGRATION_CLICKED_ADD, INTEGRATION_VIEWED_INTEGRATION } from 'commons/src/analytics/AnalyticsEvents';
import LinkButton from 'commons/src/components/buttons/LinkButton';
import SubHeader from 'commons/src/components/headers/SubHeader';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { userlane } from 'commons/src/components/placeholder';
import { linkToClaudeConnectors, linkToMcpDocs, roleRestrictions } from 'commons/src/constants';
import IntegrationRow from 'commons/src/features/integrations/IntegrationRow';
import { ActionButton } from 'commons/src/models/menuModels';
import { Store } from 'commons/src/reducers';
import { CommonRequestType } from 'commons/src/reducers/requestReducer';
import { paths } from '../../../constants';
import styles from './McpIntegration.module.scss';

const analyticsObject = {
    pageType: PageType.Integration,
    page: IntegrationType.MCP,
};

const McpIntegrationPage = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const mcpClients = useSelector((store: Store) => store.apiClients.mcpClients);
    const loading = useSelector((store: Store) => store.commonRequests[CommonRequestType.FetchMcpClients].loading);

    useEffect(() => {
        dispatch(fetchMcpClients());
        analyticsLogger(INTEGRATION_VIEWED_INTEGRATION, analyticsObject);
    }, [dispatch]);

    const goToAddClient = (): void => {
        navigate(`/${paths.addMcpClient}`);
        analyticsLogger(INTEGRATION_CLICKED_ADD, analyticsObject);
    };

    const actionButtons: ActionButton[] = [
        {
            onClick: goToAddClient,
            title: 'McpIntegration.AddClient',
            id: 'addMcpClient',
            color: 'primary',
            testAttr: 'add-mcp-client',
            icon: <MaterialIcon name="add" />,
            requiredRoleLevel: roleRestrictions.mcp,
            requiredGroupTypes: [],
        },
    ];

    const goToDetail = (id: string): void => {
        navigate(`/${paths.integrationsMcp}/${id}`);
    };

    const clientRows = mcpClients.map(client => {
        const updateActive = (active: boolean): void => {
            dispatch(updateApiClientActiveState({ ...client, active }));
        };
        const clientIdLabel = `${txt('ApiIntegration.ClientId')}: ${client.id}`;
        return (
            <IntegrationRow
                key={client.id}
                id={client.id}
                name={client.name}
                active={client.active}
                updateIntegration={updateActive}
                onClick={goToDetail}
                firstRow={clientIdLabel}
                secondRow={client.description}
            />
        );
    });

    return (
        <div>
            <SubHeader actionButtons={actionButtons} />
            <h2 className="settings__header settings__header--no-margin-top">
                {txt('McpIntegration.PageTitle')}
                <span className={styles.betaBadge}>{txt('McpIntegration.BetaBadge')}</span>
            </h2>
            <div className="settings-details-container settings-details-container--margin">
                <p>{txt('McpIntegration.PageDescription')}</p>
                <LinkButton link={linkToClaudeConnectors} title="McpIntegration.ClaudeAiTitle" />
                <LinkButton link={linkToMcpDocs} title="McpIntegration.DocsLink" />
            </div>
            <ul className="list">
                <ReactPlaceHolder ready={!(loading && mcpClients.length === 0)} customPlaceholder={userlane}>
                    {clientRows}
                </ReactPlaceHolder>
            </ul>
        </div>
    );
};

export default McpIntegrationPage;
